import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { playPresent } from "../../lib/sounds";

export const WILLY = {
  idle: "/willy/willy-idle.webp",
  blink: "/willy/willy-blink.webp",
  glance: "/willy/willy-glance.webp",
  talk: "/willy/willy-talk.webp",
  wave: "/willy/willy-wave.webp",
  think: "/willy/willy-think.webp",
  study: "/willy/willy-study.webp",
  study2: "/willy/willy-study2.webp",
  phone: "/willy/willy-phone.webp",
  happy: "/willy/willy-happy.webp",
  sorry: "/willy/willy-sorry.webp",
  tools: "/willy/willy-tools.webp",
  present: "/willy/willy-present.webp",
};
export const WILLY_LINES = {
  idle: "Ciao, sono Willy! Chiedimi pure.",
  wave: "Ciao! Sono Willy, il tecnico virtuale EasySistem.",
  think: "Mmm... ci penso un attimo...",
  study: "Un attimo, consulto i manuali...",
  phone: "Chiamo l'assistenza Custom per sicurezza...",
  talk: "Ecco cosa ho trovato...",
  happy: "Trovato! Ecco la risposta.",
  sorry: "Qui non ho una risposta certa: meglio sentire un tecnico.",
  tools: "Seguimi passo passo.",
  present: "Ecco il prodotto che fa per te!",
};
const ANIM = { idle: "willy-float", wave: "willy-wave", think: "willy-think", study: "willy-study", phone: "willy-phone", talk: "willy-talk", happy: "willy-pop", sorry: "willy-shrug", tools: "willy-pop", present: "willy-pop" };
const GESTURES = ["willy-g-tilt", "willy-g-bounce", "willy-g-look", "willy-g-stretch", "willy-g-nod"];
const LIVE = new Set(["idle", "talk"]);
const rnd = (a, b) => a + Math.random() * (b - a);

export const preloadWilly = () => Object.values(WILLY).forEach((src) => { const i = new Image(); i.src = src; });

/** Vita "procedurale" di Willy: battito di ciglia, sguardi, piccoli gesti casuali, labiale e pagine del manuale. */
const useWillyLife = (mood, hover) => {
  const [overlay, setOverlay] = useState(null);
  const [gesture, setGesture] = useState("");
  useEffect(() => {
    const timers = [];
    const later = (fn, ms) => { const t = setTimeout(fn, ms); timers.push(t); return t; };
    if (LIVE.has(mood)) {
      const blink = () => { setOverlay("blink"); later(() => { setOverlay(null); if (Math.random() < 0.25) { later(() => { setOverlay("blink"); later(() => setOverlay(null), 110); }, 160); } }, 120); later(blink, rnd(2400, 6500)); };
      later(blink, rnd(900, 3000));
      if (mood === "idle") {
        const glance = () => { setOverlay("glance"); later(() => { setOverlay("blink"); later(() => setOverlay(null), 110); }, rnd(700, 1300)); later(glance, rnd(7000, 15000)); };
        later(glance, rnd(4000, 9000));
        const gest = () => { setGesture(GESTURES[Math.floor(Math.random() * GESTURES.length)]); later(() => setGesture(""), 1700); later(gest, rnd(7000, 14000)); };
        later(gest, rnd(3500, 8000));
      }
    }
    if (mood === "talk") {
      const lips = () => { setOverlay((o) => (o === "talk" ? null : "talk")); later(lips, rnd(110, 210)); };
      later(lips, 80);
    }
    if (mood === "study") {
      const page = () => { setOverlay((o) => (o === "study2" ? null : "study2")); later(page, rnd(900, 1500)); };
      later(page, 900);
    }
    return () => { timers.forEach(clearTimeout); setOverlay(null); setGesture(""); };
  }, [mood]);
  useEffect(() => { if (hover && mood === "idle") { setGesture("willy-g-hello"); const t = setTimeout(() => setGesture(""), 1500); return () => clearTimeout(t); } }, [hover, mood]);
  return { overlay, gesture };
};

/** Inclinazione leggera verso il puntatore (solo mouse), per dare profondità. */
const useLean = (ref, enabled) => {
  const [lean, setLean] = useState("");
  useEffect(() => {
    if (!enabled || !window.matchMedia?.("(pointer: fine)").matches) return;
    let raf = 0;
    const onMove = (e) => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const r = ref.current?.getBoundingClientRect();
        if (!r) return;
        const dx = Math.max(-1, Math.min(1, (e.clientX - (r.left + r.width / 2)) / (window.innerWidth / 2)));
        const dy = Math.max(-1, Math.min(1, (e.clientY - (r.top + r.height / 2)) / (window.innerHeight / 2)));
        setLean(`rotate(${(dx * 3).toFixed(2)}deg) translate(${(dx * 3).toFixed(1)}px, ${(dy * 2).toFixed(1)}px)`);
      });
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => { window.removeEventListener("mousemove", onMove); cancelAnimationFrame(raf); };
  }, [enabled]); // eslint-disable-line
  return lean;
};

/** Mascotte Willy: espressione in base allo stato, dimensione fluida rispetto allo schermo. */
export const Willy = ({ mood = "idle", size = "clamp(64px, 9vw, 120px)", className = "", bubble, onClick, lean: leanOn = true }) => {
  const [shown, setShown] = useState(mood);
  const [swap, setSwap] = useState(false);
  const [hover, setHover] = useState(false);
  const [line, setLine] = useState(0);
  const ref = useRef(null);
  const lines = Array.isArray(bubble) ? bubble : bubble ? [bubble] : [];
  useEffect(() => {
    if (mood === shown) return;
    setSwap(true);
    const t = setTimeout(() => { setShown(mood); setSwap(false); }, 140);
    return () => clearTimeout(t);
  }, [mood]); // eslint-disable-line
  useEffect(() => { if (lines.length < 2) return; const t = setInterval(() => setLine((l) => (l + 1) % lines.length), 6000); return () => clearInterval(t); }, [lines.length]);
  const { overlay, gesture } = useWillyLife(shown, hover);
  const lean = useLean(ref, leanOn);
  const shadowAnim = shown === "idle" ? "willy-shadow-float" : "";
  return (
    <div ref={ref} className={`relative select-none ${className}`} style={{ width: size, height: size }} onClick={onClick} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} data-testid="willy" data-mood={shown} data-overlay={overlay || ""}>
      <div className={`absolute left-1/2 -translate-x-1/2 bottom-[2%] w-[58%] h-[7%] rounded-[50%] bg-[#1A237E]/25 blur-[3px] ${shadowAnim}`} aria-hidden="true" />
      <div className="w-full h-full willy-lean" style={{ transform: lean }}>
        <div className={`w-full h-full origin-bottom ${gesture}`}>
          <div className={`relative w-full h-full origin-bottom willy-breathe ${ANIM[shown] || ""} ${swap ? "scale-90 opacity-0" : "scale-100 opacity-100"} transition-[transform,opacity] duration-150`}>
            <img src={WILLY[shown] || WILLY.idle} alt={`Willy ${shown}`} draggable={false} decoding="async" className="w-full h-full object-contain drop-shadow-[0_8px_14px_rgba(26,35,126,0.28)]" />
            {overlay && <img src={WILLY[overlay]} alt="" aria-hidden="true" draggable={false} className="absolute inset-0 w-full h-full object-contain drop-shadow-[0_8px_14px_rgba(26,35,126,0.28)]" data-testid="willy-overlay" />}
          </div>
        </div>
      </div>
      {lines.length > 0 && <div key={line} className="absolute -top-2 right-full mr-2 whitespace-nowrap bg-white text-[#1d1e20] text-[12px] font-bold px-3 py-1.5 rounded-2xl rounded-br-sm shadow-lg border border-[#e0e0e0] willy-bubble" data-testid="willy-bubble">{lines[line]}</div>}
    </div>
  );
};

export default Willy;


/** Willy tiene in mano il prodotto consigliato; click → il prodotto si gira, vola verso il cliente e si apre la scheda. */
export const WillyPresents = ({ products = [], onOpen, onUnlock, size = "clamp(150px, 26vw, 210px)" }) => {
  const [main, setMain] = useState(0);
  const [flying, setFlying] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  if (!products.length) return null;
  const p = products[main];
  const open = () => {
    if (flying) return;
    setFlying(true);
    playPresent();
    timer.current = setTimeout(() => { setFlying(false); onOpen?.(p); }, 650);
  };
  return (
    <div className="flex flex-col items-center gap-1 mt-2" data-testid="willy-presents">
      <p className="self-start text-[11px] uppercase tracking-wider font-bold text-[#CF1072]">Willy consiglia</p>
      <div className="relative shrink-0" style={{ width: size, height: size }}>
        <img src={WILLY.present} alt="Willy presenta" draggable={false} className="w-full h-full object-contain drop-shadow-[0_8px_14px_rgba(26,35,126,0.28)] willy-pop" />
        <button type="button" onClick={open} title={`Apri la scheda ${p.name}`} aria-label={`Apri ${p.name}`}
          className={`absolute left-1/2 -translate-x-1/2 w-[46%] aspect-square rounded-xl bg-white border-2 border-[#CF1072]/30 shadow-xl flex items-center justify-center overflow-hidden cursor-pointer willy-product ${flying ? "willy-throw" : "willy-hold"}`}
          style={{ bottom: "19%" }} data-testid={`willy-product-${p.product_id}`}>
          <img src={p.img || "/img/logo-easysistem.png"} alt={p.name} draggable={false} className="w-full h-full object-contain p-1.5" />
        </button>
      </div>
      <div className="w-full text-center">
        <button type="button" onClick={open} className="font-bold text-[14px] text-[#1d1e20] leading-snug hover:text-[#CF1072] transition-colors duration-200" data-testid="willy-product-name">{p.name}</button>
        {p.price && <p className="text-[12px] text-[#56585e]">da {Number(p.price).toLocaleString("it-IT")} €</p>}
        {!p.price && p.price_hidden && (
          <Link to="/area-clienti" onClick={onUnlock} className="inline-flex items-center gap-1 mt-1 rounded-full bg-[#CF1072]/10 text-[#CF1072] text-[11px] font-bold px-2.5 py-1 hover:bg-[#CF1072] hover:text-white transition-colors duration-200" data-testid="willy-price-unlock">
            <Lock className="w-3 h-3" /> Registrati per vedere il prezzo
          </Link>
        )}
        <p className="text-[11px] text-[#56585e] mt-0.5">Tocca il prodotto: Willy te lo passa e apre la scheda.</p>
        {products.length > 1 && (
          <div className="flex gap-1.5 mt-2 flex-wrap justify-center" data-testid="willy-product-alts">
            {products.map((q, i) => <button type="button" key={q.product_id} onClick={() => setMain(i)} className={`w-10 h-10 rounded-lg border bg-white overflow-hidden ${i === main ? "border-[#CF1072] ring-2 ring-[#CF1072]/30" : "border-[#e0e0e0]"}`} title={q.name} data-testid={`willy-alt-${q.product_id}`}><img src={q.img || "/img/logo-easysistem.png"} alt={q.name} className="w-full h-full object-contain p-0.5" /></button>)}
          </div>
        )}
      </div>
    </div>
  );
};
